import {
  Select,
  type SelectOption,
} from '@monorepo/shared/componentsV2/Select/Select';
import type { SelectGroup } from '@monorepo/shared/componentsV2/Select/types';
import { inspectionDateProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import keyBy from 'lodash.keyby';
import { FormTemplateFieldTypes } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { useGetAllFieldsWithGroupSlugs } from './hooks/useGetAllFieldsWithGroupSlugs';
import { useGetInspectionDate } from './hooks/useGetInspectionDate';
import { FieldWithGroupSlug } from './types';

const StyledSelect = styled(Select)`
  display: flex;
  margin-bottom: 0.5em;

  label {
    margin: 0 1px;
  }
`;

const UNAVAILABLE_GROUP_NAME = 'unavailable';
const selectGroups: SelectGroup[] = [
  {
    name: UNAVAILABLE_GROUP_NAME,
    label: 'Unavailable',
    tooltip: `These fields do not meet the requirements for an inspection date.
      Inspection date fields must not be conditional or in repeatable sections.`,
  },
];

type InspectionDateSelectorProps = {
  isEditingDisabled: boolean;
};

export function InspectionDateSelector({
  isEditingDisabled,
}: InspectionDateSelectorProps) {
  const {
    formTemplate,
    formTemplateHelpers: { setInspectionDate, fieldConditionHelpers },
  } = useFormTemplateContext();

  const { inspectionDateField } = useGetInspectionDate(formTemplate);
  const fieldsWithGroupSlugs = useGetAllFieldsWithGroupSlugs(formTemplate);

  const groupBySlugMap = useMemo(
    () => keyBy(formTemplate?.groups, 'slug'),
    [formTemplate],
  );

  const dateFieldsWithGroupSlugs: FieldWithGroupSlug[] = useMemo(
    () =>
      fieldsWithGroupSlugs?.filter(
        ({ field }: FieldWithGroupSlug) =>
          field.type === FormTemplateFieldTypes.DATE,
      ) || [],
    [fieldsWithGroupSlugs],
  );

  const options: SelectOption[] = useMemo(
    () =>
      dateFieldsWithGroupSlugs.map(({ field, groupSlug }) => {
        const group = groupBySlugMap[groupSlug];
        if (!group) {
          throw new Error(`Group with slug ${groupSlug} not found.`);
        }
        const selectOption: SelectOption = {
          value: field.id,
          label: field.name,
          subText: group.name,
        };
        if (
          group.isDuplicable ||
          fieldConditionHelpers.getTriggerFieldByConditionallyShownFieldId(
            field.id,
          )
        ) {
          selectOption.disabled = true;
          selectOption.groupName = UNAVAILABLE_GROUP_NAME;
        }
        return selectOption;
      }),
    [dateFieldsWithGroupSlugs, groupBySlugMap, fieldConditionHelpers],
  );

  const handleChange: React.ComponentProps<typeof Select>['onChange'] =
    useCallback(
      (event) => {
        const newInspectionDateId = event.target.value;
        if (newInspectionDateId) {
          const newInspectionDateWithGroupSlug = dateFieldsWithGroupSlugs.find(
            ({ field }) => field.id === newInspectionDateId,
          );
          if (!newInspectionDateWithGroupSlug) {
            throw new Error('Selected inspection date not found in list.');
          }
          setInspectionDate(newInspectionDateWithGroupSlug);
        }
      },
      [dateFieldsWithGroupSlugs, setInspectionDate],
    );

  if (!formTemplate?.groups.length) return null;

  return (
    <StyledSelect
      label="Form Inspection Date"
      name="inspectionDate"
      options={options}
      groups={selectGroups}
      value={inspectionDateField?.id || ''}
      onChange={handleChange}
      error={!inspectionDateField}
      errorText={!inspectionDateField ? 'Selection required.' : undefined}
      placeholder="Choose one"
      noOptionsText="Form has no date fields"
      explanationUrl={inspectionDateProductGuideUrl}
      dontSaveSpaceForErrorText
      isReadOnly={isEditingDisabled}
    />
  );
}
